import type { MontillaLoadConfig } from '../types'
import type { MontillaPlugin, PluginContext, MontillaPublicApi } from '../types/plugin'
import type { MontillaDestination } from '../types/destination'
import type { EventBus } from '../event-bus'
import { globalSingleton, __resetGlobalSingletonForTests } from '../utils/global-singleton'

const INSTALLED_KEY = 'installed-plugins'

/**
 * Names on the built-in `montilla` object that plugins cannot overwrite.
 * Any collision with these is a hard error (plugin is skipped with a warning).
 */
const RESERVED_NAMES = new Set([
  'load', 'log', 'error', 'resolveLocation', 'track', 'trackForm', 'trackLink',
  'trackElement', 'trackSubmit', 'trackOptInPush', 'page', 'identify', 'ready',
  'isBot', 'getMetas', 'getContext', 'reset', 'use',
])

/**
 * Tracks plugins installed on the current SDK instance. Keyed by `plugin.name`
 * so duplicate registrations can be detected and `reset()` can iterate.
 */
export const installedPlugins = globalSingleton<Map<string, MontillaPlugin>>(
  INSTALLED_KEY,
  () => new Map(),
)

/**
 * Build the context object handed to a plugin's `install()`.
 */
function createPluginContext(
  plugin: MontillaPlugin,
  config: Readonly<MontillaLoadConfig>,
  eventBus: EventBus,
  montillaApi: MontillaPublicApi,
): PluginContext {
  const prefix = `[montilla:${plugin.name}]`
  return {
    montilla: montillaApi,
    config,
    logger: {
      log: (...args) => console.log(prefix, ...args),
      warn: (...args) => console.warn(prefix, ...args),
      error: (...args) => console.error(prefix, ...args),
    },
    registerDestination(destination: MontillaDestination) {
      eventBus.register(destination)
    },
    use(fn) {
      eventBus.use(fn)
    },
  }
}

/**
 * Install all plugins declared in the user's config, attaching each returned
 * API object to the `montilla` root under `plugin.name`.
 *
 * Uses `Object.defineProperty` with `writable:false, configurable:false` so
 * individual plugin namespaces are immutable after install, without requiring
 * the whole `montilla` object to be frozen.
 *
 * @param plugins   Plugin instances from `config.plugins`.
 * @param config    Resolved load config.
 * @param eventBus  Event bus (so plugins can register destinations/middlewares).
 * @param target    The `montilla` root object to attach namespaces onto.
 * @param api       Subset of the montilla public API forwarded to plugins.
 */
export async function installPlugins(
  plugins: MontillaPlugin[] | undefined,
  config: Readonly<MontillaLoadConfig>,
  eventBus: EventBus,
  target: Record<string, unknown>,
  api: MontillaPublicApi,
): Promise<void> {
  if (!plugins || plugins.length === 0) return

  for (const plugin of plugins) {
    if (!plugin?.name || typeof plugin.install !== 'function') {
      console.warn('[montilla] Invalid plugin — missing `name` or `install`:', plugin)
      continue
    }

    if (RESERVED_NAMES.has(plugin.name)) {
      console.warn(
        `[montilla] Plugin name "${plugin.name}" collides with a built-in API method and was skipped.`,
      )
      continue
    }

    if (installedPlugins.has(plugin.name)) {
      console.warn(
        `[montilla] Plugin "${plugin.name}" is already installed — ignoring duplicate.`,
      )
      continue
    }

    const ctx = createPluginContext(plugin, config, eventBus, api)

    try {
      const namespaceApi = await plugin.install(ctx)
      if (namespaceApi && typeof namespaceApi === 'object') {
        Object.defineProperty(target, plugin.name, {
          value: namespaceApi,
          writable: false,
          configurable: false,
          enumerable: true,
        })
      }
      installedPlugins.set(plugin.name, plugin)
    } catch (err) {
      console.error(`[montilla] Plugin "${plugin.name}" failed to install:`, err)
    }
  }
}

/**
 * Invoke `reset()` on every installed plugin. Errors are logged but do not
 * abort the loop — `reset()` is a best-effort cleanup.
 */
export async function resetPlugins(): Promise<void> {
  for (const plugin of installedPlugins.values()) {
    try {
      await plugin.reset?.()
    } catch (err) {
      console.error(`[montilla] Plugin "${plugin.name}" reset failed:`, err)
    }
  }
}

/**
 * Test-only helper — clears the registry so `load()` can be re-run in a
 * fresh state between tests.
 */
export function __resetPluginRegistryForTests(): void {
  installedPlugins.clear()
  __resetGlobalSingletonForTests(INSTALLED_KEY)
}
